import React, { useState, useContext, createContext, useEffect, useCallback, useRef } from 'react';
import Auth from '../Utils/Auth';
import { useAddress } from './addressHook';

import getStateManager, { destroyState } from '../StateManager.js';

const authContext = createContext();

export function useAuth() {
  return useContext(authContext);
}

export function useProvideAuth() {
  console.log('[auth] useProvideAuth');

  let u = null;
  if (process.env.REACT_APP_SKIP_LOGIN === 'true') {
    u = 'local';
  }

  const [user, setUser] = useState(u);
  const [sessionPending, setSessionPending] = useState(u === null);
  const activeUser = useRef(u);
  const address = useAddress();

  const sessionRevision = useRef(0);
  const validation = useRef(null);
  const validationRetry = useRef(null);
  const cancelValidation = useCallback(() => {
    sessionRevision.current += 1;
    clearTimeout(validation.current?.timeout);
    validation.current?.request.abort();
    validation.current = null;
    clearTimeout(validationRetry.current);
    validationRetry.current = null;
  }, []);

  const invalidateSession = useCallback(() => {
    cancelValidation();
    Auth.invalidateSession();
    activeUser.current = null;
    setUser(null);
    setSessionPending(false);
    const stateManager = getStateManager();
    stateManager.wsManager?.suspend?.('authentication-failed');
    stateManager.simulation?.stop?.();
    destroyState();
  }, [cancelValidation]);

  const acceptUser = useCallback((username) => {
    if (activeUser.current && activeUser.current !== username) {
      cancelValidation();
      const stateManager = getStateManager();
      stateManager.wsManager?.suspend?.('account-changed');
      stateManager.simulation?.stop?.();
      destroyState();
    }
    activeUser.current = username;
    setUser(username);
    setSessionPending(false);
  }, [cancelValidation]);

  const usernameUrl = address.api_url('/api/username');
  const validateSession = useCallback(() => {
    if (process.env.REACT_APP_SKIP_LOGIN === 'true') return Promise.resolve(true);
    if (validation.current) return validation.current.promise;
    clearTimeout(validationRetry.current);
    validationRetry.current = null;

    const revision = sessionRevision.current;
    const stateManager = getStateManager();
    const pending = { request: new AbortController(), promise: null };
    pending.timeout = setTimeout(() => pending.request.abort(), 10000);
    pending.promise = Auth.validateSession(usernameUrl, { signal: pending.request.signal })
      .then((result) => {
        if (revision !== sessionRevision.current || stateManager.store() !== stateManager)
          return null;
        if (result?.authenticated === true) {
          acceptUser(result.username);
          return true;
        }
        if (result?.authenticated === false) {
          invalidateSession();
          return false;
        }

        // A network failure or server error does not prove that the session expired.
        if (!activeUser.current) {
          validationRetry.current = setTimeout(() => {
            validationRetry.current = null;
            validateSession();
          }, 5000);
        }
        return null;
      })
      .finally(() => {
        clearTimeout(pending.timeout);
        if (validation.current === pending) validation.current = null;
      });
    validation.current = pending;
    return pending.promise;
  }, [usernameUrl, invalidateSession, acceptUser]);

  useEffect(() => () => cancelValidation(), [cancelValidation, usernameUrl]);

  useEffect(() => {
    if (activeUser.current === null) {
      validateSession();
    }
  }, [validateSession]);

  const signin = (username, password, otp, cb, reloadOnSuccess = false) => {
    if (process.env.REACT_APP_SKIP_LOGIN === 'true') {
      return { user: 'local' };
    }

    cancelValidation();
    return Auth.signin(
      (m) => {
        if (m === 'good') {
          cancelValidation();
          if (!reloadOnSuccess) {
            acceptUser(username);
          }
        }
        cb(m);
      },
      username,
      password,
      otp,
      address.api_url('/api/user_login')
    );
  };

  const signout = (cb) => {
    cancelValidation();
    return Auth.signout(() => {
      cancelValidation();
      activeUser.current = null;
      setUser(null);
      setSessionPending(false);
      const stateManager = getStateManager();
      stateManager.wsManager?.stop?.();
      if (stateManager.simulation) stateManager.simulation.stop();
      destroyState();

      cb();
    }, address.api_url('/api/user_logout'));
  };

  const deleteAccount = (cb) => {
    cancelValidation();
    return Auth.deleteAccount((m) => {
      if (m === 'good') {
        cancelValidation();
        activeUser.current = null;
        setUser(null);
        setSessionPending(false);
        const stateManager = getStateManager();
        stateManager.wsManager?.stop?.();
        if (stateManager.simulation) stateManager.simulation.stop();
        destroyState();
      }
      cb(m);
    }, address.api_url('/api/del_user'));
  };

  const register = (username, password, password2, email, cb) => {
    cancelValidation();
    return Auth.register(
      (m, emailSendFailed) => {
        if (m === 'good') {
          cancelValidation();
          acceptUser(username);
        }
        cb(m, emailSendFailed);
      },
      username,
      password,
      password2,
      email,
      address.api_url('/api/user_registration')
    );
  };

  return {
    user,
    sessionPending,
    signin,
    signout,
    register,
    deleteAccount,
    invalidateSession,
    validateSession
  };
}

export function ProvideAuth({ children }) {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}
